import { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { ColumnsType } from "antd/lib/table";
import { Button, Col, Divider, Form, Input, InputNumber, Row, Space, Spin, Table, Tooltip, message, notification, Popconfirm } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { PlusOutlined, ArrowRightOutlined, ArrowLeftOutlined, MinusCircleOutlined, DeleteFilled } from "@ant-design/icons";
import { fNumberToLocalString, fStringToNumber, getIdFromObject } from "utils";
import { EstSaleAsinType, EstSaleCategoryFormType, EstSaleCategoryType } from "types/data";
import { addAsin, calculateAsin, deleteCategory, fetchAllAsins, getCategory, removeAsin, updateCategory } from "services";
import { Helmet } from "react-helmet";

const initialState: EstSaleCategoryType = {
  _id: '',
  category: '',
  marketplace: '',
  version: '',
  rankRange: [],
  a: [],
  b: null,
  c: null,
  draftParams: {}
};

const initialDataState: EstSaleCategoryFormType = {
  comboField: [{
    rankRange: null,
    aVar: null,
  }],
  bVar: null,
  cVar: null,
};

type AsinCalcError = {
  asin?: string;
  message?: string;
};

const applyCalculatedAsins = (prev: any[], data: any, isLive: boolean) => {
  const errors: AsinCalcError[] = Array.isArray(data?.errors) ? data.errors : [];
  const errorByAsin = new Map(
    errors
      .filter((item) => item.asin)
      .map((item) => [item.asin as string, item.message || "Calculate failed"])
  );
  if (!data?.asins && errorByAsin.size === 0) {
    return prev;
  }
  return prev.map((item: any) => {
    const calculated = data?.asins?.[item.ASIN];
    const failedMessage = errorByAsin.get(item.ASIN);
    if (!calculated && !failedMessage) {
      return item;
    }
    const next = { ...item };
    if (calculated) {
      if (!isLive) {
        next.ourSalesdraftParams = calculated.saleDraft;
        next.saleDraftStatus = calculated.saleDraftStatus || "success";
        next.saleDraftError = calculated.saleDraftError || "";
      } else {
        next.ourSalesLive = calculated.saleV2;
        next.saleLiveStatus = calculated.saleLiveStatus || "success";
        next.saleLiveError = calculated.saleLiveError || "";
      }
    }
    if (failedMessage) {
      if (!isLive) {
        next.saleDraftStatus = "failed";
        next.saleDraftError = failedMessage;
      } else {
        next.saleLiveStatus = "failed";
        next.saleLiveError = failedMessage;
      }
    }
    return next;
  });
};

const formatLastCalculatedAt = (value?: string) => {
  if (!value) {
    return "Not calculated yet";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Not calculated yet";
  }
  return date.toLocaleString();
};

const renderCalculatedSale = (value: any, status?: string, errorMessage?: string) => {
  const text = value == null || value == "undefined" ? "N/A" : fNumberToLocalString(value);
  if (status !== "failed") {
    return text;
  }
  const title = errorMessage ? `Calculate failed: ${errorMessage}` : "Calculate failed";
  return (
    <Tooltip title={title}>
      <span style={{ color: "#cf1322" }}>{text}</span>
    </Tooltip>
  );
};

const coefficientInputProps = {
  style: { width: "100%" },
  controls: false as const,
  formatter: (value: any) => value === undefined || value === null || value === "" ? "" : `${fNumberToLocalString(value)}`,
  parser: (value: any) => fStringToNumber(value || ""),
};

const CoefficientFields = ({
  fields,
  add,
  remove,
}: {
  fields: { key: number; name: number }[];
  add: (defaultValue?: { rankRange: number; aVar: number }) => void;
  remove: (index: number) => void;
}) => (
  <div style={{ width: "100%", marginBottom: 10 }}>
    <Space style={{ marginBottom: 8 }}>
      <label><strong>Rang rank and a:</strong></label>
      <Button type="dashed" size="small" onClick={() => add({ rankRange: 0, aVar: 0 })} icon={<PlusOutlined />}>
        Add combo field
      </Button>
    </Space>
    <div style={{ overflowX: "auto", maxWidth: "100%", paddingBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, width: "max-content" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            position: "sticky",
            left: 0,
            zIndex: 1,
            background: "#fff",
            paddingRight: 4,
          }}
        >
          <strong style={{ height: 32, lineHeight: "32px" }}>R</strong>
          <strong style={{ height: 32, lineHeight: "32px" }}>a</strong>
        </div>
        {fields.map(({ key, name }) => (
          <div key={key} style={{ width: 128, flex: "0 0 128px" }}>
            <Form.Item name={[name, "rankRange"]} noStyle>
              <InputNumber {...coefficientInputProps} />
            </Form.Item>
            <div style={{ height: 8 }} />
            <Form.Item name={[name, "aVar"]} noStyle>
              <InputNumber {...coefficientInputProps} />
            </Form.Item>
            {fields.length > 1 ? (
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <MinusCircleOutlined onClick={() => remove(name)} />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const showCalculateAsinsError = (data: any) => {
  const errors: AsinCalcError[] = Array.isArray(data?.errors) ? data.errors : [];
  if (errors.length === 0) {
    message.error(data?.message || "Failed calculate asins");
    return;
  }

  const visible = errors.slice(0, 20);
  const succeeded = data?.asins ? Object.keys(data.asins).length : 0;
  notification.error({
    message: succeeded > 0
      ? `Calculated ${succeeded} ASIN(s). Failed ${errors.length} ASIN(s).`
      : `Failed to calculate ${errors.length} ASIN(s)`,
    description: (
      <div>
        {visible.map((item, index) => (
          <div key={`${item.asin || "asin"}-${index}`}>
            <strong>{item.asin || "Unknown ASIN"}</strong>
            {item.message ? `: ${item.message}` : ""}
          </div>
        ))}
        {errors.length > visible.length && (
          <div>... and {errors.length - visible.length} more</div>
        )}
      </div>
    ),
    duration: 0,
  });
};


const EstimateSaleCategory = () => {
  const { location: marketplaceParam, version: versionParam, category: categoryParam } = useParams();
  const [form] = Form.useForm()
  const [states, setStates] = useState<EstSaleCategoryType>(initialState)
  const [asins, setAsins] = useState<any>([])
  const [loadingTable, setLoadingTable] = useState<boolean>(false)
  const [loadingForm, setLoadingForm] = useState<boolean>(false)
  const [reloadDraftForm, setReloadDraftForm] = useState<boolean>(false)
  const [reloadLiveForm, setReloadLiveForm] = useState<boolean>(false)
  const [initialFormData, setInitialFormData] = useState<any>({
    draftParams: initialDataState,
    live: initialDataState
  })
  const [categoryName, setCategoryName] = useState<string>('')

  let navigate = useNavigate();

  const breadcrumbItemRender = (route: any, params: any, routes: any, paths: any) => {
    const last = routes.indexOf(route) === routes.length - 1;
    return last ? <span>{route.breadcrumbName}</span> : <Link to={`../${paths.join('/')}`}>{route.breadcrumbName}</Link>;
  }

  const routes = [
    {
      path: `${states.marketplace}/${states.version}`,
      breadcrumbName: `Estimate Sales - ${states.marketplace}`,
    },
    {
      path: '',
      breadcrumbName: categoryName,
    },
  ];

  const columns: ColumnsType<EstSaleAsinType> = [
    {
      title: 'ASIN',
      dataIndex: 'ASIN',
      key: 'ASIN',
      render: text => <a>{text}</a>,
    },
    {
      title: 'JungleScout Sales',
      dataIndex: 'jungleScoutSales',
      key: 'jungleScoutSales',
      render: value => value !== undefined && value != 'undefined' ? fNumberToLocalString(value) : null
    },
    {
      title: 'Our Sales Draft',
      dataIndex: 'ourSalesdraftParams',
      key: 'ourSalesdraftParams',
      render: (value, record) => renderCalculatedSale(value, record.saleDraftStatus, record.saleDraftError)
    },
    {
      title: 'Our Sales Live',
      dataIndex: 'ourSalesLive',
      key: 'ourSalesLive',
      render: (value, record) => renderCalculatedSale(value, record.saleLiveStatus, record.saleLiveError)
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="default"
            loading={loadingTable}
            onClick={() => handleOnCalculate(record.ASIN, false)}
          >
            Calculate Draft
          </Button>
          <Button
            type="primary"
            loading={loadingTable}
            onClick={() => handleOnCalculate(record.ASIN, true)}
          >
            Calculate Live
          </Button>
          <Popconfirm
            title="Are you sure to delete this ASIN?"
            onConfirm={() => handleOnDeleteAsin(record.ASIN)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" style={{ marginLeft: 30 }} danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getFieldData = (fields: any) => {
    let parsedComboField = {
      rankRange: [],
      a: [],
    }
    fields.comboField.forEach((field: any) => {
      parsedComboField.rankRange = parsedComboField.rankRange.concat(field.rankRange);
      parsedComboField.a = parsedComboField.a.concat(field.aVar);
    });
    return {
      rankRange: parsedComboField.rankRange,
      a: parsedComboField.a,
      b: fields.bVar,
      c: fields.cVar,
    }
  }

  const loadCategory = async (category: string, marketplace: string, version: string) =>
    await getCategory({ category, marketplace, version })
      .then(({ data }: any) => {
        const comboFieldsLive = data.rankRange.map((item: any, key: number) => ({ rankRange: item, aVar: data.a[key] }));
        setStates(() => {
          setLoadingForm(false)
          let draftData = {}
          if (Object.keys(data.draftParams).length > 0) {
            const comboFieldsDraft = data.draftParams.rankRange?.map(
              (item: any, key: number) => ({ rankRange: item, aVar: data.draftParams.a[key] })
            ) || initialFormData.comboField
            draftData = {
              comboField: comboFieldsDraft,
              bVar: data.draftParams.b || null,
              cVar: data.draftParams.c || null,
            };
          }
          setInitialFormData((prevFormData: any) => ({
            draftParams: {
              ...prevFormData.draftParams,
              ...draftData,
            },
            live: {
              comboField: comboFieldsLive,
              bVar: data.b,
              cVar: data.c
            }
          }))
          data._id = typeof data._id === 'object' ? getIdFromObject(data) : data._id
          return (data as EstSaleCategoryType);
        });
      })
      .catch(() => {
        setLoadingForm(false);
        message.error("Can not get category");
      });

  const loadListOfAsins = async (categoryId: string) => {
    return await fetchAllAsins(categoryId)
      .then(({ data }: any) => {
        let asins: any = [];
        if (Object.keys(data.asins).length > 0) {
          asins = Object.keys(data.asins).map((item: any) => {
            const asin = item;
            const values = data.asins[asin];
            return ({
              key: `asin-${asin}`,
              ASIN: asin,
              jungleScoutSales: values.jsSale,
              ourSalesdraftParams: values.saleDraft,
              ourSalesLive: values.saleV2,
              saleDraftStatus: values.saleDraftStatus,
              saleLiveStatus: values.saleLiveStatus,
              saleDraftError: values.saleDraftError,
              saleLiveError: values.saleLiveError,
            })
          });
        }
        setAsins(() => {
          setLoadingTable(false);
          return asins;
        });
      })
      .catch(() => {
        setLoadingTable(false);
        message.error("Can not load the list of asins");
      })
  };

  const handleOnSaveCategory = () => {
    setLoadingForm(true)
    const categoryId = states._id
    return updateCategory(categoryId, states)
      .then(() => {
        setCategoryName(() => {
          setLoadingForm(false)
          return states.category
        })
        message.success('Updated category successfully')
      })
      .catch(() => {
        setLoadingForm(false);
        message.error('Failed update category')
      })
      .finally(() => navigate(`../${states.marketplace}/${states.version}/${states.category}`, { replace: true }))
  };

  const handleOnDeleteCategory = () => {
    const categoryId = states._id
    return deleteCategory(categoryId)
      .then(() => {
        message.success('Deleted category successfully');
        navigate(`../${states.marketplace}/${states.version}`);
      })
      .catch(() => message.error('Failed delete category'))
  };

  const rememberLastCalculatedAt = (data: any) => {
    if (!data?.lastDraftCalculatedAt && !data?.lastLiveCalculatedAt) {
      return;
    }
    setStates((prev) => ({
      ...prev,
      ...(data.lastDraftCalculatedAt ? { lastDraftCalculatedAt: data.lastDraftCalculatedAt } : {}),
      ...(data.lastLiveCalculatedAt ? { lastLiveCalculatedAt: data.lastLiveCalculatedAt } : {}),
    }));
  };

  const handleOnCalculate = (asin: string, isLive: boolean) => {
    setLoadingTable(true);
    const params = {
      _id: states._id,
      asin,
      live: isLive
    }
    return calculateAsin(params)
      .then(({ data }) => {
        setAsins((prev: any) => {
          const newArrAsins = prev.map((item: any) => {
            if (item.ASIN !== asin) {
              return item
            }
            const calculated = data.estimationsSale?.[asin];
            return ({
              ...item,
              ourSalesLive: calculated?.saleV2,
              ourSalesdraftParams: calculated?.saleDraft,
              saleDraftStatus: calculated?.saleDraftStatus ?? item.saleDraftStatus,
              saleLiveStatus: calculated?.saleLiveStatus ?? item.saleLiveStatus,
              saleDraftError: calculated?.saleDraftError || "",
              saleLiveError: calculated?.saleLiveError || "",
            });
          })
          setLoadingTable(false);
          return newArrAsins;
        });
        message.success('Calculated asin');
        rememberLastCalculatedAt(data);
      })
      .catch((e) => {
        const response = e.response?.data;
        const failedMessage = response?.errors?.[0]?.message || response?.message || "Calculate failed";
        setAsins((prev: any) => prev.map((item: any) => {
          if (item.ASIN !== asin) {
            return item;
          }
          if (!isLive) {
            return { ...item, saleDraftStatus: "failed", saleDraftError: failedMessage };
          }
          return { ...item, saleLiveStatus: "failed", saleLiveError: failedMessage };
        }));
        setLoadingTable(false);
        rememberLastCalculatedAt(response);
        message.error(response?.message || "Failed calculate asin");
      });
  };

  const handleOnCalculateAllAsins = (isLive: boolean) => {
    setLoadingTable(true);
    const params = {
      _id: states._id,
      live: isLive
    }
    return calculateAsin(params)
      .then(({ data }) => {
        setAsins((prev: any) => applyCalculatedAsins(prev, data, isLive));
        setLoadingTable(false);
        rememberLastCalculatedAt(data);
        message.success('Calculated asins');
      })
      .catch((e) => {
        const data = e.response?.data;
        setAsins((prev: any) => applyCalculatedAsins(prev, data, isLive));
        setLoadingTable(false);
        rememberLastCalculatedAt(data);
        showCalculateAsinsError(data);
      });
  };

  const handleOnDeleteAsin = (asin: string) => {
    setLoadingTable(true);
    const params = {
      _id: states._id,
      asin,
    }
    return removeAsin(params)
      .then(() => {
        message.success('Deleted asin successfully');
        setAsins((prev: any) => {
          setLoadingTable(false);
          if (asin === '*') {
            return [];
          }
          const newArrAsins = prev.filter((item: any) => item.ASIN !== asin)
          return newArrAsins;
        });
      })
      .catch(() => {
        setLoadingTable(false);
        message.error('Failed delete asin')
      });
  };

  const handleOnValuesChangeDraft = (_: any, allFields: any) => {
    const fields = getFieldData(allFields);
    setStates((prevState) => {
      setInitialFormData((prevFormState: any) => ({
        ...prevFormState,
        draftParams: {
          comboField: allFields.comboField,
          bVar: allFields.bVar,
          cVar: allFields.cVar,
        }
      }));
      return ({
        ...prevState,
        draftParams: fields,
      })
    });
  };

  const handleOnValuesChangeLive = (_: any, allFields: any) => {
    const fields = getFieldData(allFields);
    setStates((prevState) => {
      setInitialFormData((prevFormState: any) => ({
        ...prevFormState,
        live: {
          comboField: allFields.comboField,
          bVar: allFields.bVar,
          cVar: allFields.cVar,
        }
      }));
      return ({
        ...prevState,
        ...fields,
      })
    });
  };

  const handleOnAddNewAsin = (values: any) => {
    setLoadingTable(true);
    const params = {
      _id: states._id,
      ...values,
    }
    return addAsin(params)
      .then(({ data: res }: any) => {
        message.success('Added and calculated successfully');
        rememberLastCalculatedAt(res);
        form.resetFields();
        setAsins((prev: any) => {
          const newAsin = [{
            key: `asin-${values.asin}`,
            ASIN: values.asin,
            jungleScoutSales: values.jsSale,
            ourSalesdraftParams: res.data.saleDraft,
            ourSalesLive: res.data.saleV2,
            saleDraftStatus: res.data.saleDraftStatus,
            saleLiveStatus: res.data.saleLiveStatus,
            saleDraftError: res.data.saleDraftError,
            saleLiveError: res.data.saleLiveError,
          }];
          const newArrAsins = newAsin.concat(prev);
          setLoadingTable(false);
          return newArrAsins;
        });
      })
      .catch((e) => {
        setLoadingTable(false);
        message.error(e.response?.data?.message || 'Failed calculate new asin')
      })
  };

  const handleOnCopyForm = (liveOrigin: boolean) => {
    liveOrigin ? setReloadDraftForm(true) : setReloadLiveForm(true);
    setInitialFormData((prevState: any) => {
      const dataUpdate = liveOrigin ? prevState.live : prevState.draftParams;
      const fields = getFieldData(dataUpdate);
      setStates((prevState) =>
        liveOrigin ? ({
          ...prevState,
          draftParams: fields,
        }) : ({
          ...prevState,
          ...fields,
        })
      );
      return ({
        draftParams: dataUpdate,
        live: dataUpdate,
      })
    });
  };

  useEffect(() => {
    if (categoryParam && marketplaceParam && versionParam) {
      setCategoryName(() => {
        setLoadingForm(true);
        return categoryParam
      })
      loadCategory(categoryParam, marketplaceParam, versionParam);
    }
  }, []);

  useEffect(() => {
    if (states._id !== '')
      loadListOfAsins(states._id);
  }, [states]);

  useEffect(() => {
    setReloadDraftForm(false);
  }, [reloadDraftForm])

  useEffect(() => {
    setReloadLiveForm(false);
  }, [reloadLiveForm])

  return (
    <>
      <Helmet>
        <title>{states.category} | Category - Etimate Sales</title>
      </Helmet>
      <Row gutter={[16, 16]} align="middle">
        {loadingForm ? (
          <div className="loading-page"><Spin size="large" /></div>
        ) : (
          <>
            <Col span={24}>
              <PageHeader
                className="site-page-header"
                breadcrumb={{ routes, itemRender: breadcrumbItemRender }}
                title={
                  <>
                    <Input style={{ width: 300, marginRight: 20 }} value={states.category} onChange={(e) => setStates({ ...states, category: e.target.value })} />
                    <span>{`(Version: ${states.version})`}</span>
                  </>
                }
                extra={[
                  <Button key="save-category-data" type="primary" onClick={() => handleOnSaveCategory()}>Save</Button>,
                  <Popconfirm
                    placement="bottomRight"
                    title="Are you sure to delete this category?"
                    onConfirm={() => handleOnDeleteCategory()}
                    okText="Yes"
                    cancelText="No"
                    key="delete-category"
                  >
                    <Button type="primary" icon={<DeleteFilled />} danger style={{ marginLeft: 30 }} />
                  </Popconfirm>
                ]}
              />
            </Col>
            <Col span={11} style={{ minWidth: 0 }}>
              <Divider orientation="left">DRAFT</Divider>
              {reloadDraftForm ? (
                <div className="loading-page"><Spin size="large" /></div>
              ) : (
                <Form
                  name="basic"
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  onValuesChange={handleOnValuesChangeDraft}
                  autoComplete="off"
                >
                  <Form.List
                    name="comboField"
                    initialValue={initialFormData.draftParams.comboField}
                  >
                    {(fields, { add, remove }) => (
                      <CoefficientFields fields={fields} add={add} remove={remove} />
                    )}
                  </Form.List>
                  <Form.Item
                    name="bVar"
                    initialValue={initialFormData.draftParams.bVar}
                    style={{ marginBottom: "10px" }}
                  >
                    <InputNumber
                      addonBefore={<strong>b</strong>}
                      controls={false}
                    />
                  </Form.Item>
                  <Form.Item
                    name="cVar"
                    initialValue={initialFormData.draftParams.cVar}
                    style={{ marginBottom: "10px" }}
                  >
                    <InputNumber
                      addonBefore={<strong>c</strong>}
                      controls={false}
                    />
                  </Form.Item>
                </Form>
              )}
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <Space direction="vertical">
                <Button
                  size="small"
                  disabled={!initialFormData.draftParams}
                  icon={<ArrowRightOutlined />}
                  onClick={() => handleOnCopyForm(false)}
                />
                <Button
                  size="small"
                  disabled={!initialFormData.live}
                  icon={<ArrowLeftOutlined />}
                  onClick={() => handleOnCopyForm(true)}
                />
              </Space>
            </Col>
            <Col span={11} style={{ minWidth: 0 }}>
              <Divider orientation="left">LIVE</Divider>
              {reloadLiveForm ? (
                <div className="loading-page"><Spin size="large" /></div>
              ) : (
                <Form
                  name="basic"
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  onValuesChange={handleOnValuesChangeLive}
                  autoComplete="off"
                >
                  <Form.List
                    name="comboField"
                    initialValue={initialFormData.live.comboField}
                  >
                    {(fields, { add, remove }) => (
                      <CoefficientFields fields={fields} add={add} remove={remove} />
                    )}
                  </Form.List>
                  <Form.Item
                    name="bVar"
                    initialValue={initialFormData.live.bVar}
                    style={{ marginBottom: "10px" }}
                  >
                    <InputNumber
                      addonBefore={<strong>b</strong>}
                      controls={false}
                    />
                  </Form.Item>
                  <Form.Item
                    name="cVar"
                    initialValue={initialFormData.live.cVar}
                    style={{ marginBottom: "10px" }}
                  >
                    <InputNumber
                      addonBefore={<strong>c</strong>}
                      controls={false}
                    />
                  </Form.Item>
                </Form>
              )}
            </Col>
          </>
        )}
      </Row>
      <Divider />
      <Space direction="vertical" size="middle">
        <Space direction="horizontal">
          <label><strong>Test ASINs:</strong></label>
          <Popconfirm
            title="Are you sure to delete all of ASINs?"
            onConfirm={() => handleOnDeleteAsin('*')}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger>Delete all ASINs</Button>
          </Popconfirm>
          <Button type="primary" onClick={() => handleOnCalculateAllAsins(false)}>Calculate all ASINs DRAFT</Button>
          <Button type="primary" onClick={() => handleOnCalculateAllAsins(true)}>Calculate all ASINs LIVE</Button>
        </Space>
        <div>
          Last draft calculation: {formatLastCalculatedAt(states.lastDraftCalculatedAt)}
          {" · "}
          Last live calculation: {formatLastCalculatedAt(states.lastLiveCalculatedAt)}
        </div>
        <Form
          form={form}
          name="calculateNew"
          autoComplete="off"
          layout="inline"
          onFinish={handleOnAddNewAsin}
          style={{ marginBottom: "20px" }}
        >
          <Form.Item name="asin">
            <Input required placeholder="New ASIN" />
          </Form.Item>
          <Form.Item name="jsSale" style={{ width: 150 }}>
            <InputNumber
              required
              placeholder="JungleScout Sales"
              formatter={value => `${fNumberToLocalString(value)}`}
              parser={value => fStringToNumber(value)}
              controls={false}

            />
          </Form.Item>
          <Button type="primary" htmlType="submit">Calculate</Button>
        </Form>
      </Space>
      <Table
        columns={columns}
        dataSource={asins}
        loading={loadingTable}
        pagination={false}
        size="middle"
      />
    </>
  );
}

export default EstimateSaleCategory;